import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { COLORS } from '../../Styles/Colors';

export const SUB_SIDEBAR_WIDTH = 280;

const Panel = styled.div<{ $open: boolean }>`
  width: ${SUB_SIDEBAR_WIDTH}px;
  min-width: ${SUB_SIDEBAR_WIDTH}px;
  max-width: ${SUB_SIDEBAR_WIDTH}px;
  height: 100vh;
  overflow: hidden;
  border-left: 0.5px solid ${COLORS.lightGray};
  background: ${COLORS.darkGray};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  transform: translateX(${props => (props.$open ? '0' : '-100%')});
  opacity: ${props => (props.$open ? 1 : 0)};
  transition:
    transform 0.28s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => (props.$open ? 'auto' : 'none')};
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 20px 10px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: ${COLORS.darkGray};
  }

  &::-webkit-scrollbar-thumb {
    background: ${COLORS.lightGray};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: ${COLORS.teal};
  }
`;

const Title = styled.div`
  padding: 10px 15px;
  font-size: 16px;
  font-weight: bold;
  color: white;
  text-align: center;
  margin-bottom: 10px;
  position: sticky;
  top: 0;
  background: ${COLORS.darkGray};
  z-index: 1;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  animation: subNavContentIn 0.22s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes subNavContentIn {
    from {
      opacity: 0;
      transform: translateX(-10px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

interface SubSidebarPanelProps {
  open: boolean;
  title: string;
  contentKey: string;
  panelRef?: React.Ref<HTMLDivElement>;
  children: React.ReactNode;
}

const SubSidebarPanel: React.FC<SubSidebarPanelProps> = ({
  open,
  title,
  contentKey,
  panelRef,
  children,
}) => {
  const [mounted, setMounted] = useState(open);

  useEffect(() => {
    if (open) {
      setMounted(true);
    }
  }, [open]);

  useEffect(() => {
    if (!open && mounted) {
      const timer = window.setTimeout(() => setMounted(false), 280);
      return () => window.clearTimeout(timer);
    }
  }, [open, mounted]);

  if (!mounted) {
    return null;
  }

  return (
    <Panel ref={panelRef} $open={open}>
      <ScrollArea>
        <Title>{title}</Title>
        <Content key={contentKey}>{children}</Content>
      </ScrollArea>
    </Panel>
  );
};

export default SubSidebarPanel;
